import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Col, Container, Form, Modal, Row } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Swal from 'sweetalert2';
import {
  FaBalanceScale,
  FaCashRegister,
  FaCheckCircle,
  FaClipboardCheck,
  FaClock,
  FaFilePdf,
  FaRedoAlt,
  FaSearch,
  FaUsers,
} from 'react-icons/fa';
import Navigation from './Navigation';
import AdminPageHeader from './AdminPageHeader';
import AdminDataTable from './AdminDataTable';
import useAuth from '../../hooks/useAuth';
import { isManagementUser, readStoredAdminUser } from './adminAccessControl';
import {
  API_BASE_URL,
  buildAuthHeaders,
  fetchScopedOutlets,
  formatCurrency,
  formatDateForInput,
  getInitialScopedOutlets,
  isSuccessResponse,
  parseJsonResponse,
  resolveAdminSessionToken,
  resolveCurrentUserOutlet,
} from './posBackofficeHelpers';
import './PosCatalogManager.css';
import './StaffShiftClosingPage.css';

const emptySummary = {
  total_closings: 0,
  total_transactions: 0,
  total_net_sales_amount: 0,
  total_expected_cash_amount: 0,
  total_actual_cash_amount: 0,
  total_cash_difference_amount: 0,
};

const formatCount = value => Number(value || 0).toLocaleString('id-ID');

const getResponseMessage = (response, data, fallback) => {
  if (typeof data?.message === 'string' && data.message.trim()) {
    return data.message;
  }

  if (typeof data?.error === 'string' && data.error.trim() && data.error !== '0') {
    return data.error;
  }

  if (response?.status === 403) {
    return 'Anda tidak memiliki akses ke modul Staff Shift Closing.';
  }

  return fallback;
};

const formatDateTime = value => {
  if (!value) {
    return '-';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }

  return parsed.toLocaleString('id-ID', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatShiftPeriod = (start, end) => {
  if (!start || !end) {
    return '-';
  }

  return `${formatDateTime(start)} - ${formatDateTime(end)}`;
};

const formatAmountInput = value => {
  const normalized = Math.round(Number(value || 0) * 100) / 100;
  return Number.isInteger(normalized) ? String(normalized) : normalized.toFixed(2);
};

const getDifferenceTone = value => {
  if (Number(value || 0) === 0) {
    return 'match';
  }
  return Number(value || 0) > 0 ? 'positive' : 'negative';
};

const getDifferenceLabel = value => {
  if (Number(value || 0) === 0) {
    return 'Sesuai';
  }
  return Number(value || 0) > 0 ? 'Lebih' : 'Kurang';
};

const getClosingSourceLabel = preview => {
  const channels = [];

  if (preview?.includes_membership) {
    channels.push('Membership');
  }
  if (preview?.includes_class_sales) {
    channels.push('Class Sale');
  }
  if (preview?.includes_pt_sales) {
    channels.push('PT Sales');
  }
  if (preview?.includes_pos) {
    channels.push('POS');
  }

  if (channels.length > 0) {
    return channels.join(' + ');
  }
  return 'Transaksi outlet';
};

const getClosingScopeDescription = preview => {
  const outletTypeLabel = preview?.outlet_type_label || 'Outlet';
  const channels = [];
  if (preview?.includes_membership) {
    channels.push('membership');
  }
  if (preview?.includes_class_sales) {
    channels.push('class sale');
  }
  if (preview?.includes_pt_sales) {
    channels.push('PT sales');
  }
  if (preview?.includes_pos) {
    channels.push('POS');
  }
  if (channels.length > 0) {
    return `${outletTypeLabel} ini menutup transaksi ${channels.join(', ')} yang dibuat staf aktif dalam satu closing staff.`;
  }
  return 'Scope closing mengikuti konfigurasi tipe outlet aktif.';
};

const getPaymentMethodChannelDetail = item => {
  const segments = [];

  if (Number(item?.membership_transaction_count || 0) > 0) {
    segments.push(
      `${Number(item.membership_transaction_count || 0).toLocaleString('id-ID')} membership (${formatCurrency(item.membership_amount)})`
    );
  }

  if (Number(item?.class_transaction_count || 0) > 0) {
    segments.push(
      `${Number(item.class_transaction_count || 0).toLocaleString('id-ID')} class (${formatCurrency(item.class_amount)})`
    );
  }

  if (Number(item?.pt_transaction_count || 0) > 0) {
    segments.push(
      `${Number(item.pt_transaction_count || 0).toLocaleString('id-ID')} PT (${formatCurrency(item.pt_amount)})`
    );
  }

  if (Number(item?.pos_transaction_count || 0) > 0) {
    segments.push(
      `${Number(item.pos_transaction_count || 0).toLocaleString('id-ID')} POS (${formatCurrency(item.pos_amount)})`
    );
  }

  if (segments.length === 0) {
    return `${Number(item?.transaction_count || 0).toLocaleString('id-ID')} transaksi`;
  }

  return segments.join(' • ');
};

const buildPaymentMethodSummaryOverview = paymentMethods =>
  (Array.isArray(paymentMethods) ? paymentMethods : []).reduce(
    (accumulator, item) => ({
      methodCount: accumulator.methodCount + 1,
      transactionCount: accumulator.transactionCount + Number(item?.transaction_count || 0),
      amount: accumulator.amount + Number(item?.amount || 0),
    }),
    {
      methodCount: 0,
      transactionCount: 0,
      amount: 0,
    }
  );

const createEmptyPaymentMethodDetailData = overrides => ({
  outlet_id: 0,
  cashier_id: 0,
  cashier_name: '',
  period_start: '',
  period_end: '',
  outlet_type: '',
  outlet_type_label: '',
  includes_membership: false,
  includes_class_sales: false,
  includes_pt_sales: false,
  includes_pos: false,
  payment_method_key: '',
  payment_method_label: 'Semua metode bayar',
  transaction_count: 0,
  total_amount: 0,
  membership_transaction_count: 0,
  membership_amount: 0,
  class_transaction_count: 0,
  class_amount: 0,
  pt_transaction_count: 0,
  pt_amount: 0,
  pos_transaction_count: 0,
  pos_amount: 0,
  transactions: [],
  ...(overrides || {}),
});

const createEmptyPaymentMethodDetailState = overrides => ({
  show: false,
  loading: false,
  error: '',
  data: createEmptyPaymentMethodDetailData(),
  ...(overrides || {}),
});

const sanitizePdfFileSegment = value => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return normalized || 'staff-closing';
};

const buildStaffShiftClosingPdfFilename = ({ record, preview, outletName, staffName }) => {
  const referenceDate = record?.closed_at || preview?.period_end || new Date().toISOString();
  const parsedReferenceDate = new Date(referenceDate);
  const dateKey = Number.isNaN(parsedReferenceDate.getTime())
    ? 'closing'
    : `${parsedReferenceDate.getFullYear()}${String(parsedReferenceDate.getMonth() + 1).padStart(2, '0')}${String(parsedReferenceDate.getDate()).padStart(2, '0')}`;

  return `staff-closing-${sanitizePdfFileSegment(outletName)}-${sanitizePdfFileSegment(staffName)}-${dateKey}.pdf`;
};

const exportStaffShiftClosingPdf = reportPayload => {
  const record = reportPayload?.record || null;
  const preview = reportPayload?.preview || null;
  if (!record || !preview) {
    return false;
  }

  const outletLabel = reportPayload?.outletName || `Outlet #${preview.outlet_id || record.outlet_id || '-'}`;
  const staffLabel = record.cashier_name || preview.cashier_name || reportPayload?.staffLabel || `Staff #${record.cashier_id || preview.cashier_id || '-'}`;
  const generatedByLabel = reportPayload?.generatedByName || '-';
  const sourceLabel = getClosingSourceLabel(preview);
  const paymentMethods = Array.isArray(preview.payment_method_summary) ? preview.payment_method_summary : [];
  const paymentMethodOverview = buildPaymentMethodSummaryOverview(paymentMethods);
  const differenceValue = Number(record.cash_difference_amount || 0);
  const differenceTone = getDifferenceTone(differenceValue);

  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'pt',
    format: 'a4',
  });

  doc.setFontSize(16);
  doc.text('Laporan Closing Staff Harian', 40, 36);
  doc.setFontSize(10);
  doc.text(
    `Outlet: ${outletLabel} | Staff: ${staffLabel} | Closed at: ${formatDateTime(record.closed_at)}`,
    40,
    56
  );
  doc.text(
    `Periode: ${formatShiftPeriod(preview.period_start, preview.period_end)} | Scope: ${sourceLabel} | Dibuat oleh: ${generatedByLabel}`,
    40,
    72
  );

  autoTable(doc, {
    startY: 92,
    theme: 'grid',
    head: [['Ringkasan', 'Nilai', 'Catatan']],
    body: [
      [
        'Total transaksi',
        formatCount(record.transaction_count || preview.transaction_count),
        `Net sales ${formatCurrency(record.net_sales_amount || preview.net_sales_amount)}`,
      ],
      [
        'Membership',
        preview.includes_membership ? formatCurrency(preview.membership_sales_amount) : 'Tidak termasuk',
        preview.includes_membership
          ? `${formatCount(preview.membership_transaction_count)} transaksi membership`
          : 'Outlet ini tidak menghitung membership pada closing staff.',
      ],
      [
        'Class sale',
        preview.includes_class_sales ? formatCurrency(preview.class_sales_amount) : 'Tidak termasuk',
        preview.includes_class_sales
          ? `${formatCount(preview.class_transaction_count)} transaksi class sale`
          : 'Outlet ini tidak menghitung class sale pada closing staff.',
      ],
      [
        'PT sales',
        preview.includes_pt_sales ? formatCurrency(preview.pt_sales_amount) : 'Tidak termasuk',
        preview.includes_pt_sales
          ? `${formatCount(preview.pt_transaction_count)} transaksi PT sales`
          : 'Outlet ini tidak menghitung PT sales pada closing staff.',
      ],
      [
        'POS',
        preview.includes_pos ? formatCurrency(preview.pos_sales_amount) : 'Tidak termasuk',
        preview.includes_pos
          ? `${formatCount(preview.pos_transaction_count)} transaksi POS`
          : 'Outlet ini tidak menghitung POS pada closing staff.',
      ],
      [
        'Cash sales',
        formatCurrency(record.cash_sales_amount || preview.cash_sales_amount),
        `${formatCount(record.cash_transaction_count || preview.cash_transaction_count)} transaksi tunai`,
      ],
      [
        'Non cash sales',
        formatCurrency(record.non_cash_sales_amount || preview.non_cash_sales_amount),
        `${formatCount(record.non_cash_transaction_count || preview.non_cash_transaction_count)} transaksi non tunai`,
      ],
      [
        'Semua metode bayar',
        formatCurrency(paymentMethodOverview.amount),
        `${formatCount(paymentMethodOverview.transactionCount)} transaksi pada ${formatCount(paymentMethodOverview.methodCount)} metode pembayaran`,
      ],
      [
        'Expected cash',
        formatCurrency(record.expected_cash_amount || preview.expected_cash_amount),
        'Kas tunai yang seharusnya diterima staff pada periode ini.',
      ],
      [
        'Actual cash',
        formatCurrency(record.actual_cash_amount),
        'Kas aktual yang dicatat saat closing shift disimpan.',
      ],
      [
        'Selisih kas',
        `${getDifferenceLabel(differenceValue)} ${formatCurrency(Math.abs(differenceValue))}`,
        differenceTone === 'match'
          ? 'Kas aktual sesuai dengan expected cash.'
          : differenceTone === 'positive'
            ? 'Kas aktual lebih besar daripada expected cash.'
            : 'Kas aktual lebih kecil daripada expected cash.',
      ],
      ['Catatan', record.note || '-', 'Catatan serah-terima / operasional shift.'],
    ],
    styles: {
      fontSize: 10,
      cellPadding: 8,
      valign: 'middle',
    },
    headStyles: {
      fillColor: [32, 37, 45],
      textColor: [255, 255, 255],
    },
    columnStyles: {
      0: { cellWidth: 170 },
      1: { cellWidth: 165 },
      2: { cellWidth: 360 },
    },
  });

  if (paymentMethods.length > 0) {
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 18,
      theme: 'striped',
      head: [['Metode bayar', 'Total nominal', 'Total transaksi', 'Membership', 'Class Sale', 'PT Sales', 'POS']],
      body: paymentMethods.map(item => [
        item.label || 'Other',
        formatCurrency(item.amount),
        formatCount(item.transaction_count),
        Number(item.membership_transaction_count || 0) > 0
          ? `${formatCount(item.membership_transaction_count)} trx • ${formatCurrency(item.membership_amount)}`
          : '-',
        Number(item.class_transaction_count || 0) > 0
          ? `${formatCount(item.class_transaction_count)} trx • ${formatCurrency(item.class_amount)}`
          : '-',
        Number(item.pt_transaction_count || 0) > 0
          ? `${formatCount(item.pt_transaction_count)} trx • ${formatCurrency(item.pt_amount)}`
          : '-',
        Number(item.pos_transaction_count || 0) > 0
          ? `${formatCount(item.pos_transaction_count)} trx • ${formatCurrency(item.pos_amount)}`
          : '-',
      ]),
      styles: {
        fontSize: 9,
        cellPadding: 7,
        valign: 'middle',
      },
      headStyles: {
        fillColor: [22, 101, 52],
        textColor: [255, 255, 255],
      },
    });
  }

  const footerY = Math.min((doc.lastAutoTable?.finalY || 120) + 22, 560);
  doc.setFontSize(9);
  doc.text('Dokumen ini dibuat otomatis dari Adminfitness saat closing shift disimpan.', 40, footerY);

  doc.save(
    buildStaffShiftClosingPdfFilename({
      record,
      preview,
      outletName: outletLabel,
      staffName: staffLabel,
    })
  );

  return true;
};

function StaffShiftClosingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { verifyAndRefreshToken } = useAuth();
  const currentUser = useMemo(() => readStoredAdminUser(), []);
  const canManageWorkspace = isManagementUser(currentUser);
  const ownOutlet = useMemo(() => resolveCurrentUserOutlet(currentUser), [currentUser]);
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const queryOutletId = useMemo(() => queryParams.get('outlet_id') || '', [queryParams]);
  const queryCashierId = useMemo(
    () => queryParams.get('cashier_id') || queryParams.get('staff_id') || '',
    [queryParams]
  );
  const currentUserStaffId = String(
    currentUser?.userid ??
    currentUser?.Userid ??
    currentUser?.id ??
    currentUser?.ID ??
    ''
  ).trim();
  const initialOutlets = useMemo(() => {
    if (!canManageWorkspace && ownOutlet?.id) {
      return [ownOutlet];
    }

    const scoped = getInitialScopedOutlets(currentUser);
    if (scoped.length > 0) {
      return scoped;
    }

    return ownOutlet?.id ? [ownOutlet] : [];
  }, [canManageWorkspace, currentUser, ownOutlet]);

  const [authToken, setAuthToken] = useState('');
  const [outlets, setOutlets] = useState(initialOutlets);
  const [staffOptions, setStaffOptions] = useState([]);
  const [selectedOutlet, setSelectedOutlet] = useState(queryOutletId || ownOutlet?.id || initialOutlets[0]?.id || '');
  const [selectedStaff, setSelectedStaff] = useState(canManageWorkspace ? queryCashierId || '' : currentUserStaffId);
  const [dateRange, setDateRange] = useState({
    date_from: formatDateForInput(new Date(new Date().setDate(new Date().getDate() - 29))),
    date_to: formatDateForInput(new Date()),
  });
  const [search, setSearch] = useState('');
  const [preview, setPreview] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [summary, setSummary] = useState(emptySummary);
  const [totalRows, setTotalRows] = useState(0);
  const [loadingStaffOptions, setLoadingStaffOptions] = useState(false);
  const [loadingWorkspace, setLoadingWorkspace] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [closingDraft, setClosingDraft] = useState({
    actual_cash_amount: '',
    note: '',
  });
  const [lastSubmittedClosingReport, setLastSubmittedClosingReport] = useState(null);
  const [paymentMethodDetail, setPaymentMethodDetail] = useState(createEmptyPaymentMethodDetailState);
  const paymentMethodDetailRequestRef = useRef(0);

  const selectedOutletName = useMemo(
    () => outlets.find(outlet => String(outlet.id) === String(selectedOutlet))?.name || 'Outlet belum dipilih',
    [outlets, selectedOutlet]
  );

  const selectedStaffOption = useMemo(
    () => staffOptions.find(option => String(option.staff_id) === String(selectedStaff)) || null,
    [selectedStaff, staffOptions]
  );

  const selectedStaffLabel = selectedStaffOption?.staff_name || currentUser?.full_name || currentUser?.username || 'Staff aktif';
  const latestClosing = historyRows[0] || null;
  const actualCashValue = Number(closingDraft.actual_cash_amount || 0);
  const differenceAmount = preview ? actualCashValue - Number(preview.expected_cash_amount || 0) : 0;
  const differenceTone = getDifferenceTone(differenceAmount);
  const previewSourceLabel = useMemo(() => getClosingSourceLabel(preview), [preview]);
  const previewScopeDescription = useMemo(() => getClosingScopeDescription(preview), [preview]);
  const previewPaymentMethodSummary = useMemo(
    () => (Array.isArray(preview?.payment_method_summary) ? preview.payment_method_summary : []),
    [preview]
  );
  const previewPaymentMethodOverview = useMemo(
    () => buildPaymentMethodSummaryOverview(previewPaymentMethodSummary),
    [previewPaymentMethodSummary]
  );
  const paymentMethodDetailRows = useMemo(
    () => (Array.isArray(paymentMethodDetail.data?.transactions) ? paymentMethodDetail.data.transactions : []),
    [paymentMethodDetail.data]
  );

  const performAuthorizedPost = useCallback(
    async (path, payload, fallbackMessage) => {
      const response = await fetch(`${API_BASE_URL}${path}`, {
        method: 'POST',
        headers: buildAuthHeaders(authToken, {
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify(payload),
      });

      const data = await parseJsonResponse(response);
      if (!response.ok || !isSuccessResponse(data?.error)) {
        throw new Error(getResponseMessage(response, data, fallbackMessage));
      }

      return data;
    },
    [authToken]
  );

  const loadStaffOptions = useCallback(async () => {
    if (!authToken || !selectedOutlet) {
      setStaffOptions([]);
      if (canManageWorkspace) {
        setSelectedStaff('');
      }
      return;
    }

    setLoadingStaffOptions(true);
    try {
      const data = await performAuthorizedPost(
        'staff-shift-closing/staff-options',
        {
          outlet: Number(selectedOutlet || 0),
          cashier_id: canManageWorkspace ? 0 : Number(currentUserStaffId || 0),
          limit: 150,
        },
        'Gagal memuat daftar staff aktif.'
      );

      const records = Array.isArray(data?.results) ? data.results : [];
      setStaffOptions(records);
      setSelectedStaff(previousValue => {
        const preserved = records.some(option => String(option.staff_id) === String(previousValue));
        if (preserved) {
          return previousValue;
        }

        if (canManageWorkspace && queryCashierId) {
          const queriedRecord = records.find(option => String(option.staff_id) === String(queryCashierId));
          if (queriedRecord) {
            return String(queriedRecord.staff_id);
          }
        }

        if (!canManageWorkspace && currentUserStaffId) {
          const ownRecord = records.find(option => String(option.staff_id) === String(currentUserStaffId));
          if (ownRecord) {
            return String(ownRecord.staff_id);
          }
        }

        return records[0]?.staff_id ? String(records[0].staff_id) : '';
      });
    } catch (error) {
      console.error('Failed to load staff options:', error);
      setStaffOptions([]);
      setSelectedStaff(canManageWorkspace ? '' : currentUserStaffId);
      setErrorMessage(error.message || 'Gagal memuat daftar staff aktif.');
    } finally {
      setLoadingStaffOptions(false);
    }
  }, [
    authToken,
    canManageWorkspace,
    currentUserStaffId,
    performAuthorizedPost,
    queryCashierId,
    selectedOutlet,
  ]);

  const loadWorkspace = useCallback(async () => {
    if (!authToken || !selectedOutlet) {
      setPreview(null);
      setHistoryRows([]);
      setSummary(emptySummary);
      setTotalRows(0);
      return;
    }

    setLoadingWorkspace(true);
    setErrorMessage('');
    try {
      const historyPromise = performAuthorizedPost(
        'staff-shift-closing/list',
        {
          outlet: Number(selectedOutlet || 0),
          cashier_id: selectedStaff ? Number(selectedStaff) : 0,
          date_from: dateRange.date_from,
          date_to: dateRange.date_to,
          search: search.trim(),
          limit: 50,
          offset: 0,
        },
        'Gagal memuat histori closing staff.'
      );

      const previewPromise = selectedStaff
        ? performAuthorizedPost(
            'staff-shift-closing/current',
            {
              outlet: Number(selectedOutlet || 0),
              cashier_id: Number(selectedStaff),
              cashier_name: selectedStaffOption?.staff_name || '',
            },
            'Gagal memuat preview closing shift.'
          )
        : Promise.resolve(null);

      const [historyData, previewData] = await Promise.all([historyPromise, previewPromise]);

      setHistoryRows(Array.isArray(historyData?.results) ? historyData.results : []);
      setSummary(historyData?.summary || emptySummary);
      setTotalRows(Number(historyData?.total || 0));
      setPreview(previewData?.data || null);
    } catch (error) {
      console.error('Failed to load staff shift closing workspace:', error);
      setPreview(null);
      setHistoryRows([]);
      setSummary(emptySummary);
      setTotalRows(0);
      setErrorMessage(error.message || 'Gagal memuat workspace closing staff.');
    } finally {
      setLoadingWorkspace(false);
    }
  }, [
    authToken,
    dateRange.date_from,
    dateRange.date_to,
    performAuthorizedPost,
    search,
    selectedOutlet,
    selectedStaff,
    selectedStaffOption?.staff_name,
  ]);

  useEffect(() => {
    let isMounted = true;

    const bootstrapPage = async () => {
      const token = await resolveAdminSessionToken(verifyAndRefreshToken, navigate);
      if (!token || !isMounted) {
        return;
      }

      setAuthToken(token);

      if (!canManageWorkspace) {
        const scopedOutlets = ownOutlet?.id ? [ownOutlet] : initialOutlets;
        setOutlets(scopedOutlets);
        if (scopedOutlets[0]?.id) {
          setSelectedOutlet(String(scopedOutlets[0].id));
        }
        return;
      }

      try {
        const scopedOutlets = await fetchScopedOutlets({ token, currentUser });
        if (!isMounted) {
          return;
        }

        setOutlets(scopedOutlets);
        setSelectedOutlet(previousValue => {
          const preferredOutlet = previousValue || queryOutletId || ownOutlet?.id || scopedOutlets[0]?.id || '';
          const exists = scopedOutlets.some(outlet => String(outlet.id) === String(preferredOutlet));
          return exists ? String(preferredOutlet) : String(scopedOutlets[0]?.id || '');
        });
      } catch (error) {
        if (!isMounted) {
          return;
        }
        console.error('Failed to load scoped outlets:', error);
        setErrorMessage(error.message || 'Gagal memuat outlet yang dapat diakses.');
      }
    };

    bootstrapPage();

    return () => {
      isMounted = false;
    };
  }, [
    canManageWorkspace,
    currentUser,
    initialOutlets,
    navigate,
    ownOutlet,
    queryOutletId,
    verifyAndRefreshToken,
  ]);

  useEffect(() => {
    if (!queryOutletId || outlets.length === 0) {
      return;
    }

    const outletExists = outlets.some(outlet => String(outlet.id) === String(queryOutletId));
    if (outletExists) {
      setSelectedOutlet(String(queryOutletId));
    }
  }, [outlets, queryOutletId]);

  useEffect(() => {
    if (!authToken || !selectedOutlet) {
      return;
    }

    void loadStaffOptions();
  }, [authToken, loadStaffOptions, selectedOutlet]);

  useEffect(() => {
    if (!canManageWorkspace || !queryCashierId || staffOptions.length === 0) {
      return;
    }

    const queriedRecord = staffOptions.find(option => String(option.staff_id) === String(queryCashierId));
    if (queriedRecord) {
      setSelectedStaff(String(queriedRecord.staff_id));
    }
  }, [canManageWorkspace, queryCashierId, staffOptions]);

  useEffect(() => {
    setClosingDraft({
      actual_cash_amount: '',
      note: '',
    });
    setLastSubmittedClosingReport(null);
    setPaymentMethodDetail(createEmptyPaymentMethodDetailState());
  }, [selectedOutlet, selectedStaff]);

  useEffect(() => {
    if (preview && !closingDraft.actual_cash_amount) {
      setClosingDraft(previousValue => ({
        ...previousValue,
        actual_cash_amount: formatAmountInput(preview.expected_cash_amount),
      }));
    }
  }, [closingDraft.actual_cash_amount, preview]);

  useEffect(() => {
    if (!authToken || !selectedOutlet) {
      return;
    }

    void loadWorkspace();
  }, [authToken, loadWorkspace, selectedOutlet, selectedStaff]);

  const handleRefresh = async () => {
    await loadStaffOptions();
    await loadWorkspace();
  };

  const handleDownloadClosingPdf = useCallback(
    reportPayload => {
      const resolvedPayload = reportPayload || lastSubmittedClosingReport;
      if (!resolvedPayload) {
        Swal.fire({
          icon: 'info',
          title: 'PDF belum tersedia',
          text: 'Simpan closing shift terlebih dahulu untuk membuat laporan PDF harian.',
        });
        return;
      }

      exportStaffShiftClosingPdf(resolvedPayload);
    },
    [lastSubmittedClosingReport]
  );

  const closePaymentMethodDetail = useCallback(() => {
    paymentMethodDetailRequestRef.current += 1;
    setPaymentMethodDetail(createEmptyPaymentMethodDetailState());
  }, []);

  const handleOpenPaymentMethodDetail = useCallback(
    async (paymentMethodKey = '', paymentMethodLabel = 'Semua metode bayar') => {
      if (!selectedStaff) {
        Swal.fire({
          icon: 'warning',
          title: 'Staff belum dipilih',
          text: 'Pilih staff aktif terlebih dahulu sebelum melihat detail transaksi shift.',
        });
        return;
      }

      if (!preview) {
        Swal.fire({
          icon: 'warning',
          title: 'Preview belum siap',
          text: 'Refresh preview closing staff terlebih dahulu supaya detail transaksi sesuai shift aktif.',
        });
        return;
      }

      const placeholderData = createEmptyPaymentMethodDetailData({
        outlet_id: Number(selectedOutlet || 0),
        cashier_id: Number(selectedStaff || 0),
        cashier_name: selectedStaffOption?.staff_name || selectedStaffLabel,
        payment_method_key: paymentMethodKey || '',
        payment_method_label: paymentMethodLabel || 'Semua metode bayar',
        period_start: preview.period_start,
        period_end: preview.period_end,
        outlet_type: preview.outlet_type,
        outlet_type_label: preview.outlet_type_label,
        includes_membership: Boolean(preview.includes_membership),
        includes_class_sales: Boolean(preview.includes_class_sales),
        includes_pt_sales: Boolean(preview.includes_pt_sales),
        includes_pos: Boolean(preview.includes_pos),
      });

      setPaymentMethodDetail(
        createEmptyPaymentMethodDetailState({
          show: true,
          loading: true,
          data: placeholderData,
        })
      );
      const requestID = paymentMethodDetailRequestRef.current + 1;
      paymentMethodDetailRequestRef.current = requestID;

      try {
        const response = await performAuthorizedPost(
          'staff-shift-closing/payment-method-detail',
          {
            outlet: Number(selectedOutlet || 0),
            cashier_id: Number(selectedStaff || 0),
            cashier_name: selectedStaffOption?.staff_name || selectedStaffLabel,
            payment_method_key: paymentMethodKey || '',
          },
          'Gagal memuat detail transaksi metode bayar.'
        );
        if (paymentMethodDetailRequestRef.current !== requestID) {
          return;
        }

        setPaymentMethodDetail(
          createEmptyPaymentMethodDetailState({
            show: true,
            loading: false,
            data: createEmptyPaymentMethodDetailData(response?.data || placeholderData),
          })
        );
      } catch (error) {
        if (paymentMethodDetailRequestRef.current !== requestID) {
          return;
        }
        console.error('Failed to load payment method detail:', error);
        setPaymentMethodDetail(
          createEmptyPaymentMethodDetailState({
            show: true,
            loading: false,
            error: error.message || 'Gagal memuat detail transaksi metode bayar.',
            data: placeholderData,
          })
        );
      }
    },
    [
      performAuthorizedPost,
      preview,
      selectedOutlet,
      selectedStaff,
      selectedStaffLabel,
      selectedStaffOption?.staff_name,
    ]
  );

  const handleCreateClosing = async () => {
    if (!selectedStaff) {
      Swal.fire({
        icon: 'warning',
        title: 'Staff belum dipilih',
        text: 'Pilih staff aktif terlebih dahulu sebelum menyimpan closing shift.',
      });
      return;
    }

    if (!preview) {
      Swal.fire({
        icon: 'warning',
        title: 'Preview belum siap',
        text: 'Silakan refresh data closing staff lalu coba lagi.',
      });
      return;
    }

    if (actualCashValue < 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Kas aktual tidak valid',
        text: 'Nominal kas aktual tidak boleh negatif.',
      });
      return;
    }

    const confirmation = await Swal.fire({
      icon: 'question',
      title: 'Simpan closing shift?',
      text: `Closing akan mencatat ${preview.transaction_count} transaksi ${previewSourceLabel} pada shift aktif ${selectedStaffLabel}.`,
      showCancelButton: true,
      confirmButtonText: 'Simpan closing',
      cancelButtonText: 'Batal',
      confirmButtonColor: '#0d6efd',
    });

    if (!confirmation.isConfirmed) {
      return;
    }

    setSubmitting(true);
    try {
      const createResponse = await performAuthorizedPost(
        'staff-shift-closing/create',
        {
          outlet: Number(selectedOutlet || 0),
          cashier_id: Number(selectedStaff || 0),
          cashier_name: selectedStaffOption?.staff_name || selectedStaffLabel,
          actual_cash_amount: actualCashValue,
          note: closingDraft.note.trim(),
        },
        'Gagal menyimpan closing shift.'
      );

	      const createdRecord = createResponse?.data || null;
	      const createdPreview = createResponse?.preview || preview;
	      const responseMessage = createResponse?.message || 'Pencatatan closing staff berhasil disimpan. Anda bisa langsung unduh PDF laporan harian shift ini.';
	      const responseWarning = String(createResponse?.warning || '').trim();
	      const closingReportPayload = createdRecord && createdPreview
	        ? {
            record: createdRecord,
            preview: createdPreview,
            outletName: selectedOutletName,
            staffLabel: createdRecord.cashier_name || selectedStaffLabel,
            generatedByName:
              currentUser?.full_name ||
              currentUser?.fullname ||
              currentUser?.username ||
              'Adminfitness',
          }
        : null;

      setClosingDraft({
        actual_cash_amount: '',
        note: '',
      });
      setLastSubmittedClosingReport(closingReportPayload);

      await loadWorkspace();

      if (closingReportPayload) {
	        const successResult = await Swal.fire({
	          icon: 'success',
	          title: 'Closing shift tersimpan',
	          text: responseMessage,
	          footer: responseWarning ? `<small>${responseWarning}</small>` : undefined,
	          showCancelButton: true,
	          confirmButtonText: 'Download PDF',
	          cancelButtonText: 'Tutup',
          confirmButtonColor: '#0d6efd',
        });

        if (successResult.isConfirmed) {
          handleDownloadClosingPdf(closingReportPayload);
        }
      } else {
	        Swal.fire({
	          icon: 'success',
	          title: 'Closing shift tersimpan',
	          text: createResponse?.message || 'Pencatatan closing staff berhasil disimpan di adminfitness.',
	          footer: responseWarning ? `<small>${responseWarning}</small>` : undefined,
	          timer: 1800,
	          showConfirmButton: false,
	        });
      }
    } catch (error) {
      console.error('Failed to create staff shift closing:', error);
      Swal.fire({
        icon: 'error',
        title: 'Closing shift gagal disimpan',
        text: error.message || 'Terjadi kesalahan saat menyimpan closing shift.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const historyColumns = useMemo(
    () => [
      {
        name: 'Waktu Closing',
        sortable: true,
        minWidth: '180px',
        cell: row => (
          <div className="table-identity__meta">
            <strong>{formatDateTime(row.closed_at)}</strong>
            <small>{formatShiftPeriod(row.period_start, row.period_end)}</small>
          </div>
        ),
      },
      {
        name: 'Staff',
        sortable: true,
        minWidth: '180px',
        cell: row => (
          <div className="table-identity__meta">
            <strong>{row.cashier_name || `Staff #${row.cashier_id}`}</strong>
            <small>ID {row.cashier_id}</small>
          </div>
        ),
      },
      {
        name: 'Transaksi',
        sortable: true,
        width: '130px',
        cell: row => (
          <div className="table-identity__meta">
            <strong>{Number(row.transaction_count || 0).toLocaleString('id-ID')}</strong>
            <small>{formatCurrency(row.net_sales_amount)}</small>
          </div>
        ),
      },
      {
        name: 'Expected Cash',
        sortable: true,
        minWidth: '150px',
        cell: row => formatCurrency(row.expected_cash_amount),
      },
      {
        name: 'Actual Cash',
        sortable: true,
        minWidth: '150px',
        cell: row => formatCurrency(row.actual_cash_amount),
      },
      {
        name: 'Selisih',
        sortable: true,
        minWidth: '140px',
        cell: row => {
          const value = Number(row.cash_difference_amount || 0);
          const tone = getDifferenceTone(value);
          return (
            <span className={`staff-shift-closing-status is-${tone}`}>
              {getDifferenceLabel(value)} {formatCurrency(Math.abs(value))}
            </span>
          );
        },
      },
      {
        name: 'Catatan',
        grow: 1.6,
        wrap: true,
        cell: row => <span className="staff-shift-closing-note">{row.note || '-'}</span>,
      },
    ],
    []
  );

  const paymentMethodDetailColumns = useMemo(
    () => [
      {
        name: 'Tanggal',
        sortable: true,
        minWidth: '190px',
        cell: row => (
          <div className="table-identity__meta">
            <strong>{formatDateTime(row.occurred_at)}</strong>
            <small>{row.reference_no || `#${row.transaction_id}`}</small>
          </div>
        ),
      },
      {
        name: 'Sumber',
        sortable: true,
        width: '150px',
        cell: row => (
          <div className="table-identity__meta">
            <span className={`staff-shift-closing-channel-badge is-${row.source || 'other'}`}>
              {row.source_label || 'Transaksi'}
            </span>
            <small>{row.payment_method_label || 'Other'}</small>
          </div>
        ),
      },
      {
        name: 'Relasi',
        sortable: true,
        minWidth: '180px',
        cell: row => (
          <div className="table-identity__meta">
            <strong>{row.party_name || '-'}</strong>
            <small>ID #{row.transaction_id || '-'}</small>
          </div>
        ),
      },
      {
        name: 'Deskripsi',
        grow: 1.6,
        wrap: true,
        cell: row => (
          <div className="table-identity__meta">
            <strong>{row.description || 'Tanpa deskripsi'}</strong>
            <small>{row.note || row.reference_no || '-'}</small>
          </div>
        ),
      },
      {
        name: 'Nominal',
        sortable: true,
        minWidth: '150px',
        cell: row => formatCurrency(row.amount),
      },
    ],
    []
  );

  const metricCards = useMemo(
    () => [
      {
        key: 'total-closing',
        label: 'Total closing',
        value: Number(summary.total_closings || 0).toLocaleString('id-ID'),
        note: `${Number(totalRows || 0).toLocaleString('id-ID')} record tampil`,
        icon: FaClipboardCheck,
      },
      {
        key: 'transactions',
        label: 'Total transaksi',
        value: Number(summary.total_transactions || 0).toLocaleString('id-ID'),
        note: 'Akumulasi closing sesuai scope outlet pada histori terfilter',
        icon: FaUsers,
      },
      {
        key: 'expected',
        label: 'Expected cash',
        value: formatCurrency(summary.total_expected_cash_amount),
        note: 'Total kas tunai dari transaksi yang masuk ke closing staff',
        icon: FaCashRegister,
      },
      {
        key: 'difference',
        label: 'Selisih kas',
        value: formatCurrency(summary.total_cash_difference_amount),
        note: 'Akumulasi selisih actual cash vs expected cash',
        icon: FaBalanceScale,
      },
    ],
    [summary, totalRows]
  );

  return (
    <>
      <Navigation />
      <Container fluid className="admin-page-shell mt-4 px-4 pb-5">
        <AdminPageHeader
          icon={FaCashRegister}
          eyebrow="Sales & Payments"
          title="Staff Shift Closing"
          description="Pusat closing shift staf untuk memantau transaksi membership, class sale, PT sales, dan POS sesuai tipe outlet, mencatat kas aktual, dan menyimpan histori closing per staff langsung dari adminfitness."
          breadcrumbs={[
            { label: 'Dashboard', to: '/dashboardadmin' },
            { label: 'Staff Shift Closing' },
          ]}
          meta={(
            <div className="d-flex flex-wrap gap-2">
              <span className="soft-stat">
                <FaUsers /> {selectedStaff ? selectedStaffLabel : 'Semua staff'}
              </span>
              <span className="soft-stat">
                <FaClock /> {selectedOutletName}
              </span>
            </div>
          )}
          actions={(
            <div className="d-flex flex-wrap gap-2">
              <Button variant="outline-primary" onClick={() => navigate('/staff-transaction-summary')}>
                <FaClipboardCheck /> Staff Transaction Summary
              </Button>
              <Button variant="outline-secondary" onClick={handleRefresh} disabled={loadingWorkspace || loadingStaffOptions || submitting}>
                <FaRedoAlt /> Refresh
              </Button>
            </div>
          )}
        />

        <div className="staff-shift-closing-shell">
          <section className="section-card pos-catalog-form-card">
            <div className="pos-catalog-workspace-head">
              <div>
                <span className="page-eyebrow">Workspace filter</span>
                <h2>Filter closing staff</h2>
                <p>
                  Pilih outlet, staff, periode histori, lalu gunakan workspace ini untuk melihat preview shift aktif dan menyimpan closing membership, POS, atau gabungan keduanya sesuai tipe outlet.
                </p>
              </div>
            </div>

            {errorMessage ? (
              <Alert variant="danger" className="mb-0">
                {errorMessage}
              </Alert>
            ) : null}

            <Row className="g-3 align-items-end">
              <Col xl={3} md={6}>
                <Form.Group>
                  <Form.Label>Outlet</Form.Label>
                  <Form.Select
                    value={selectedOutlet}
                    onChange={event => setSelectedOutlet(event.target.value)}
                    disabled={!canManageWorkspace && Boolean(ownOutlet?.id)}
                  >
                    <option value="">Pilih outlet</option>
                    {outlets.map(outlet => (
                      <option key={outlet.id} value={outlet.id}>
                        {outlet.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col xl={3} md={6}>
                <Form.Group>
                  <Form.Label>Staff aktif</Form.Label>
                  <Form.Select
                    value={selectedStaff}
                    onChange={event => setSelectedStaff(event.target.value)}
                    disabled={loadingStaffOptions || !selectedOutlet || !canManageWorkspace}
                  >
                    <option value="">
                      {canManageWorkspace ? 'Pilih staff aktif' : 'Staff login aktif'}
                    </option>
                    {staffOptions.map(option => (
                      <option key={option.staff_id} value={option.staff_id}>
                        {option.staff_name} - {option.job_title}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col xl={2} md={6}>
                <Form.Group>
                  <Form.Label>Tanggal awal</Form.Label>
                  <Form.Control
                    type="date"
                    value={dateRange.date_from}
                    onChange={event => setDateRange(previousValue => ({ ...previousValue, date_from: event.target.value }))}
                  />
                </Form.Group>
              </Col>

              <Col xl={2} md={6}>
                <Form.Group>
                  <Form.Label>Tanggal akhir</Form.Label>
                  <Form.Control
                    type="date"
                    value={dateRange.date_to}
                    onChange={event => setDateRange(previousValue => ({ ...previousValue, date_to: event.target.value }))}
                  />
                </Form.Group>
              </Col>

              <Col xl={2} md={12}>
                <Form.Group>
                  <Form.Label>Pencarian histori</Form.Label>
                  <div className="d-flex align-items-center gap-2">
                    <Form.Control
                      type="text"
                      value={search}
                      placeholder="Staff, catatan, ID..."
                      onChange={event => setSearch(event.target.value)}
                    />
                    <Button variant="outline-secondary" onClick={handleRefresh} disabled={loadingWorkspace || loadingStaffOptions || submitting}>
                      <FaSearch />
                    </Button>
                  </div>
                </Form.Group>
              </Col>
            </Row>
          </section>

          <section className="staff-shift-closing-kpis">
            {metricCards.map(item => (
              <div key={item.key} className="section-card staff-shift-closing-kpi-card">
                <div className="staff-shift-closing-kpi-card__head">
                  <span>{item.label}</span>
                  <span className="staff-shift-closing-kpi-card__icon">
                    <item.icon />
                  </span>
                </div>
                <strong>{item.value}</strong>
                <p>{item.note}</p>
              </div>
            ))}
          </section>

          <section className="staff-shift-closing-main">
            <div className="section-card staff-shift-closing-card">
              <div className="staff-shift-closing-card__head">
                <div>
                  <span className="page-eyebrow">Preview shift aktif</span>
                  <h2>Form closing staff</h2>
                  <p>
                    {selectedStaff
                      ? `Baca transaksi aktif ${selectedStaffLabel}, masukkan kas aktual, lalu simpan closing sesuai tipe outlet saat pergantian shift.`
                      : 'Pilih staff aktif terlebih dahulu untuk membuka preview closing shift.'}
                  </p>
                </div>
              </div>

              {!selectedStaff ? (
                <Alert variant="info" className="mb-0">
                  Pilih staff aktif agar sistem bisa menghitung periode shift berjalan dan expected cash berdasarkan tipe outlet.
                </Alert>
              ) : loadingWorkspace ? (
                <Alert variant="light" className="mb-0">
                  Memuat preview closing shift...
                </Alert>
              ) : preview ? (
                <>
                  <div className="staff-shift-closing-scope-banner">
                    <div>
                      <span className="page-eyebrow">Scope closing</span>
                      <h3>{previewSourceLabel}</h3>
                      <p>{previewScopeDescription}</p>
                    </div>
                    <span className="staff-shift-closing-scope-banner__badge">
                      {preview.outlet_type_label || 'Outlet'}
                    </span>
                  </div>

                  <div className="staff-shift-closing-preview-grid">
                    <div className="staff-shift-closing-preview-item">
                      <span>Periode aktif</span>
                      <strong>{formatShiftPeriod(preview.period_start, preview.period_end)}</strong>
                      <small>
                        {preview.last_closed_at
                          ? `Closing sebelumnya ${formatDateTime(preview.last_closed_at)}`
                          : 'Belum ada closing sebelumnya pada periode ini.'}
                      </small>
                    </div>
                    <div className="staff-shift-closing-preview-item">
                      <span>Total transaksi</span>
                      <strong>{Number(preview.transaction_count || 0).toLocaleString('id-ID')}</strong>
                      <small>Net sales {formatCurrency(preview.net_sales_amount)}</small>
                    </div>
                    <div className="staff-shift-closing-preview-item">
                      <span>Membership</span>
                      <strong>
                        {preview.includes_membership
                          ? formatCurrency(preview.membership_sales_amount)
                          : 'Tidak termasuk'}
                      </strong>
                      <small>
                        {preview.includes_membership
                          ? `${Number(preview.membership_transaction_count || 0).toLocaleString('id-ID')} transaksi membership`
                          : 'Outlet ini tidak menghitung membership pada closing staff.'}
                      </small>
                    </div>
                    <div className="staff-shift-closing-preview-item">
                      <span>Class sale</span>
                      <strong>
                        {preview.includes_class_sales
                          ? formatCurrency(preview.class_sales_amount)
                          : 'Tidak termasuk'}
                      </strong>
                      <small>
                        {preview.includes_class_sales
                          ? `${Number(preview.class_transaction_count || 0).toLocaleString('id-ID')} transaksi class sale`
                          : 'Outlet ini tidak menghitung class sale pada closing staff.'}
                      </small>
                    </div>
                    <div className="staff-shift-closing-preview-item">
                      <span>PT sales</span>
                      <strong>
                        {preview.includes_pt_sales
                          ? formatCurrency(preview.pt_sales_amount)
                          : 'Tidak termasuk'}
                      </strong>
                      <small>
                        {preview.includes_pt_sales
                          ? `${Number(preview.pt_transaction_count || 0).toLocaleString('id-ID')} transaksi PT sales`
                          : 'Outlet ini tidak menghitung PT sales pada closing staff.'}
                      </small>
                    </div>
                    <div className="staff-shift-closing-preview-item">
                      <span>POS</span>
                      <strong>
                        {preview.includes_pos
                          ? formatCurrency(preview.pos_sales_amount)
                          : 'Tidak termasuk'}
                      </strong>
                      <small>
                        {preview.includes_pos
                          ? `${Number(preview.pos_transaction_count || 0).toLocaleString('id-ID')} transaksi POS`
                          : 'Outlet ini tidak menghitung POS pada closing staff.'}
                      </small>
                    </div>
                    <div className="staff-shift-closing-preview-item">
                      <span>Cash sales</span>
                      <strong>{formatCurrency(preview.cash_sales_amount)}</strong>
                      <small>{Number(preview.cash_transaction_count || 0).toLocaleString('id-ID')} transaksi tunai</small>
                    </div>
                    <div className="staff-shift-closing-preview-item">
                      <span>Pengurang kas shift</span>
                      <strong>{formatCurrency(preview.coach_payout_cash_amount)}</strong>
                      <small>
                        {Number(preview.coach_payout_cash_count || 0).toLocaleString('id-ID')} payout coach cash ditandai mengurangi shift
                      </small>
                    </div>
                    <div className="staff-shift-closing-preview-item">
                      <span>Non cash sales</span>
                      <strong>{formatCurrency(preview.non_cash_sales_amount)}</strong>
                      <small>{Number(preview.non_cash_transaction_count || 0).toLocaleString('id-ID')} transaksi non tunai</small>
                    </div>
                    <div className="staff-shift-closing-preview-item">
                      <span>Semua metode bayar</span>
                      <strong>{formatCurrency(previewPaymentMethodOverview.amount)}</strong>
                      <small>
                        {formatCount(previewPaymentMethodOverview.transactionCount)} transaksi pada {formatCount(previewPaymentMethodOverview.methodCount)} metode pembayaran
                      </small>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="staff-shift-closing-inline-action"
                        onClick={() => handleOpenPaymentMethodDetail('', 'Semua metode bayar')}
                        disabled={loadingWorkspace || paymentMethodDetail.loading || previewPaymentMethodOverview.transactionCount <= 0}
                      >
                        <FaSearch /> Lihat detail
                      </Button>
                    </div>
                  </div>

                  <div className="staff-shift-closing-method-summary">
                    <div className="staff-shift-closing-method-summary__head">
                      <div>
                        <span className="page-eyebrow">Payment methods</span>
                        <h3>Ringkasan metode bayar</h3>
                        <p>Gunakan daftar ini untuk cross-check total transaksi setiap metode sebelum closing disimpan.</p>
                      </div>
                      <div className="staff-shift-closing-method-summary__overview">
                        <strong>{formatCurrency(previewPaymentMethodOverview.amount)}</strong>
                        <small>
                          {formatCount(previewPaymentMethodOverview.transactionCount)} transaksi • {formatCount(previewPaymentMethodOverview.methodCount)} metode
                        </small>
                      </div>
                    </div>

                    {previewPaymentMethodSummary.length === 0 ? (
                      <Alert variant="light" className="mb-0">
                        Belum ada transaksi yang bisa dikelompokkan ke payment method pada periode aktif ini.
                      </Alert>
                    ) : (
                      <div className="staff-shift-closing-method-list">
                        {previewPaymentMethodSummary.map(item => (
                          <article key={item.key || item.label} className="staff-shift-closing-method-item">
                            <div className="staff-shift-closing-method-item__copy">
                              <span>{item.label || 'Other'}</span>
                              <strong>{formatCurrency(item.amount)}</strong>
                              <small>{getPaymentMethodChannelDetail(item)}</small>
                            </div>
                            <div className="staff-shift-closing-method-item__side">
                              <div className="staff-shift-closing-method-item__meta">
                                <strong>{Number(item.transaction_count || 0).toLocaleString('id-ID')}</strong>
                                <small>transaksi</small>
                              </div>
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                className="staff-shift-closing-inline-action"
                                onClick={() => handleOpenPaymentMethodDetail(item.key || '', item.label || 'Other')}
                                disabled={loadingWorkspace || paymentMethodDetail.loading || Number(item.transaction_count || 0) <= 0}
                              >
                                <FaSearch /> Lihat detail
                              </Button>
                            </div>
                          </article>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className={`staff-shift-closing-difference is-${differenceTone}`}>
                    <span>Selisih kas</span>
                    <strong>{formatCurrency(Math.abs(differenceAmount))}</strong>
                    <p>
                      {differenceTone === 'match'
                        ? 'Kas aktual sesuai dengan expected cash.'
                        : differenceTone === 'positive'
                          ? 'Kas aktual lebih besar daripada expected cash.'
                          : 'Kas aktual lebih kecil daripada expected cash.'}
                    </p>
                  </div>

                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Expected cash</Form.Label>
                        <Form.Control value={formatCurrency(preview.expected_cash_amount)} readOnly />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Kas aktual</Form.Label>
                        <Form.Control
                          type="number"
                          min={0}
                          step="0.01"
                          value={closingDraft.actual_cash_amount}
                          onChange={event => setClosingDraft(previousValue => ({
                            ...previousValue,
                            actual_cash_amount: event.target.value,
                          }))}
                          placeholder="Masukkan kas aktual"
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={12}>
                      <Form.Group>
                        <Form.Label>Catatan closing</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={4}
                          value={closingDraft.note}
                          onChange={event => setClosingDraft(previousValue => ({
                            ...previousValue,
                            note: event.target.value,
                          }))}
                          placeholder="Contoh: setor kas ke supervisor, ada selisih, atau catatan pergantian shift."
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={12}>
                      <div className="d-flex flex-wrap gap-2">
                        <Button variant="primary" onClick={handleCreateClosing} disabled={submitting || loadingWorkspace}>
                          <FaCheckCircle /> {submitting ? 'Menyimpan closing...' : 'Simpan Closing Shift'}
                        </Button>
                        {lastSubmittedClosingReport ? (
                          <Button
                            variant="outline-danger"
                            onClick={() => handleDownloadClosingPdf()}
                            disabled={submitting || loadingWorkspace}
                          >
                            <FaFilePdf /> PDF Closing Terakhir
                          </Button>
                        ) : null}
                        <Button
                          variant="outline-secondary"
                          onClick={() => setClosingDraft(previousValue => ({
                            ...previousValue,
                            actual_cash_amount: formatAmountInput(preview.expected_cash_amount),
                          }))}
                          disabled={submitting || loadingWorkspace}
                        >
                          Samakan dengan expected cash
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </>
              ) : (
                <Alert variant="light" className="mb-0">
                  Preview closing belum tersedia untuk staff ini.
                </Alert>
              )}
            </div>

            <div className="section-card staff-shift-closing-card">
              <div className="staff-shift-closing-card__head">
                <div>
                  <span className="page-eyebrow">Snapshot staff</span>
                  <h3>Konteks kerja aktif</h3>
                  <p>Ringkasan cepat untuk membaca scope closing sesuai tipe outlet dan histori terakhir staff yang sedang dipilih.</p>
                </div>
              </div>

              <div className="staff-shift-closing-spotlight">
                <div className="staff-shift-closing-spotlight__item">
                  <span className="page-eyebrow">Outlet</span>
                  <strong>{selectedOutletName}</strong>
                  <p>{preview?.outlet_type_label || selectedStaffOption?.outlet_name || ownOutlet?.name || 'Outlet staff aktif'}</p>
                </div>
                <div className="staff-shift-closing-spotlight__item">
                  <span className="page-eyebrow">Staff</span>
                  <strong>{selectedStaff ? selectedStaffLabel : 'Semua staff outlet'}</strong>
                  <p>{selectedStaffOption?.job_title || currentUser?.role || 'Staff aktif'}</p>
                </div>
                <div className="staff-shift-closing-spotlight__item">
                  <span className="page-eyebrow">Scope closing</span>
                  <strong>{previewSourceLabel}</strong>
                  <p>{previewScopeDescription}</p>
                </div>
                <div className="staff-shift-closing-spotlight__item">
                  <span className="page-eyebrow">Closing terakhir</span>
                  <strong>{latestClosing ? formatDateTime(latestClosing.closed_at) : 'Belum ada histori'}</strong>
                  <p>
                    {latestClosing
                      ? `${formatCurrency(latestClosing.actual_cash_amount)} • ${getDifferenceLabel(latestClosing.cash_difference_amount)}`
                      : 'Simpan closing pertama untuk mulai membangun histori.'}
                  </p>
                </div>
                <div className="staff-shift-closing-spotlight__item">
                  <span className="page-eyebrow">Histori terfilter</span>
                  <strong>{Number(totalRows || 0).toLocaleString('id-ID')} record</strong>
                  <p>{formatCurrency(summary.total_actual_cash_amount)} actual cash pada periode terpilih.</p>
                </div>
              </div>
            </div>
          </section>

          <section className="section-card table-card">
            <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-3">
              <div>
                <span className="page-eyebrow">Histori closing</span>
                <h2 className="mb-1">Riwayat closing staff</h2>
                <p className="mb-0 text-muted">
                  Audit histori closing berdasarkan staff, periode, total transaksi sesuai tipe outlet, nominal expected cash, actual cash, dan catatan serah-terima shift.
                </p>
              </div>
              <span className="soft-stat">
                <FaClock /> {Number(totalRows || 0).toLocaleString('id-ID')} closing
              </span>
            </div>

            <AdminDataTable
              columns={historyColumns}
              data={historyRows}
              progressPending={loadingWorkspace}
              pagination={false}
              tableMinWidth="1180px"
              emptyTitle="Belum ada histori closing"
              emptyDescription="Simpan closing shift pertama dari halaman ini untuk mulai membangun histori pencatatan staff."
            />
          </section>
        </div>
      </Container>

      <Modal show={paymentMethodDetail.show} onHide={closePaymentMethodDetail} size="xl" centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title>
            Detail transaksi {paymentMethodDetail.data?.payment_method_label || 'Semua metode bayar'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="staff-shift-closing-detail-summary">
            <div className="staff-shift-closing-detail-summary__item">
              <span>Outlet / staff</span>
              <strong>{selectedOutletName}</strong>
              <small>{selectedStaffLabel}</small>
            </div>
            <div className="staff-shift-closing-detail-summary__item">
              <span>Periode shift</span>
              <strong>{formatShiftPeriod(paymentMethodDetail.data?.period_start, paymentMethodDetail.data?.period_end)}</strong>
              <small>{paymentMethodDetail.data?.outlet_type_label || preview?.outlet_type_label || 'Outlet aktif'}</small>
            </div>
            <div className="staff-shift-closing-detail-summary__item">
              <span>Total transaksi</span>
              <strong>{formatCount(paymentMethodDetail.data?.transaction_count)}</strong>
              <small>{paymentMethodDetail.data?.payment_method_label || 'Semua metode bayar'}</small>
            </div>
            <div className="staff-shift-closing-detail-summary__item">
              <span>Total nominal</span>
              <strong>{formatCurrency(paymentMethodDetail.data?.total_amount)}</strong>
              <small>Urutan terbaru ke terlama</small>
            </div>
            <div className="staff-shift-closing-detail-summary__item">
              <span>Channel</span>
              <strong>
                {formatCount(paymentMethodDetail.data?.membership_transaction_count)} membership / {formatCount(paymentMethodDetail.data?.pos_transaction_count)} POS
              </strong>
              <small>
                {formatCurrency(paymentMethodDetail.data?.membership_amount)} / {formatCurrency(paymentMethodDetail.data?.pos_amount)}
              </small>
            </div>
          </div>

          {paymentMethodDetail.error ? (
            <Alert variant="danger">
              {paymentMethodDetail.error}
            </Alert>
          ) : null}

          <AdminDataTable
            columns={paymentMethodDetailColumns}
            data={paymentMethodDetailRows}
            progressPending={paymentMethodDetail.loading}
            pagination
            tableMinWidth="980px"
            emptyTitle="Belum ada transaksi pada metode bayar ini"
            emptyDescription="Saat transaksi membership atau POS mulai masuk ke shift aktif, detailnya akan muncul di tabel ini."
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closePaymentMethodDetail}>
            Tutup
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default StaffShiftClosingPage;
